import ActionType from './action-type'
import * as Actions from './actions'
import {type ThunkAction} from 'redux-thunk'
import {REST} from '../../../api'
import {Api} from '../../../api/Api'
import {type AppState} from '../../types/app-state'
import {
    type AuditEntry,
    AuditLogAuditType,
    AuditLogPageType,
    AuditLogSourceType,
    AuditVisibilityType,
    DownloadFileType,
} from './state'
import {type GuidType} from '../../../values/generic-type-defintions'
import {type SortColumnType} from '../../../pages/vessels-beta/contexts/types/sort-column'
import {
    type TimestampFilterType,
    getTypeDisplayName,
} from '../../../pages/vessels-beta/contexts/types/timestamp-filter'
import {PossibleColumnsVesselBeta} from '../../../pages/vessels-beta/contexts/types/paged-vessels-beta-state'
import {type NodeValue} from '../../../values/nodes/NodeData'
import {PageType} from '../../../components/vessel-filters/data-helper'
import {type AxiosResponse} from 'axios'
import {deferTillUserIsAvailable} from '../current-user/selectors'

interface AuditLogApiResponse {
    data: AuditEntry[]
}

const AUDIT_ENDPOINT = '/audit/api/v1/audits'

const requestAuditLog = (): Actions.RequestAuditLogAction => ({
    type: ActionType.REQUEST_AUDIT_LOG,
})

const setAuditLog = (data: AuditEntry[]): Actions.ReceivedAuditLogAction => ({
    type: ActionType.RECEIVED_AUDIT_LOG,
    payload: data,
})

export function fetchAuditLog(userId: GuidType): ThunkAction<void, AppState, Api, Actions.Action> {
    return (dispatch) => {
        dispatch(requestAuditLog())

        REST.post<AuditLogApiResponse>(`${AUDIT_ENDPOINT}/find`, {
            userId: userId,
            pagination: {
                pageSize: 20,
                pageOffset: 0,
            },
            orderBy: {column: 'created_at', isAscending: false},
            visibility: AuditVisibilityType.ALL,
        })
            .then((response: AxiosResponse<AuditLogApiResponse>) => {
                dispatch(setAuditLog(response.data.data))
            })
            .catch(() => dispatch(setAuditLog([] as AuditEntry[])))
    }
}

export function logPageAccess(
    currentPage: AuditLogPageType,
    additionalIdentifiers?: string,
): ThunkAction<void, AppState, Api, Actions.Action> {
    const formattedAdditionalInfo = additionalIdentifiers ? ` ${additionalIdentifiers}` : ''
    return (_dispatch, getState) => {
        deferTillUserIsAvailable(getState, () => {
            const currentUserId = getState().currentUser.currentUser.user
            void REST.post(AUDIT_ENDPOINT, {
                type: AuditLogAuditType.PAGE_ACCESS,
                message: `Viewed ${currentPage} page${formattedAdditionalInfo}`,
                source: AuditLogSourceType.UI,
                userId: currentUserId,
            })
        })
    }
}

export function logDetailsOpen(
    currentPage: AuditLogPageType,
    auditType: AuditLogAuditType,
    detailId: GuidType,
): ThunkAction<void, AppState, Api, Actions.Action> {
    return (_dispatch, getState) => {
        deferTillUserIsAvailable(getState, () => {
            const currentUserId = getState().currentUser.currentUser.user
            void REST.post(AUDIT_ENDPOINT, {
                type: auditType,
                message: `Opened ${detailId} on ${currentPage}`,
                source: AuditLogSourceType.UI,
                userId: currentUserId,
            })
        })
    }
}

export function logAssetOverlayOpen(
    currentPage: AuditLogPageType,
    auditType: AuditLogAuditType,
    detailId: GuidType,
): ThunkAction<void, AppState, Api, Actions.Action> {
    return (_dispatch, getState) => {
        deferTillUserIsAvailable(getState, () => {
            const currentUserId = getState().currentUser.currentUser.user
            void REST.post(AUDIT_ENDPOINT, {
                type: auditType,
                message: `Opened asset overlay ${detailId} from ${currentPage}`,
                source: AuditLogSourceType.UI,
                userId: currentUserId,
            })
        })
    }
}

export function logAssetOverlayTabClick(
    currentPage: AuditLogPageType,
    auditType: AuditLogAuditType,
    detailId: GuidType,
    tab: string,
): ThunkAction<void, AppState, Api, Actions.Action> {
    return (_dispatch, getState) => {
        deferTillUserIsAvailable(getState, () => {
            const currentUserId = getState().currentUser.currentUser.user
            void REST.post(AUDIT_ENDPOINT, {
                type: auditType,
                message: `Clicked ${tab} tab on asset overlay from ${currentPage} for ${detailId}`,
                source: AuditLogSourceType.UI,
                userId: currentUserId,
            })
        })
    }
}

export function logVesselSettings(
    currentPage: AuditLogPageType,
    auditType: AuditLogAuditType,
    detailId: GuidType,
): ThunkAction<void, AppState, Api, Actions.Action> {
    return (_dispatch, getState) => {
        deferTillUserIsAvailable(getState, () => {
            const currentUserId = getState().currentUser.currentUser.user
            void REST.post(AUDIT_ENDPOINT, {
                type: auditType,
                message: `Opened Vessel Settings for ${detailId} on ${currentPage}`,
                source: AuditLogSourceType.UI,
                userId: currentUserId,
            })
        })
    }
}

export function logInterlinksClick(
    targetPage: AuditLogPageType,
    auditType: AuditLogAuditType,
    detailId: GuidType,
): ThunkAction<void, AppState, Api, Actions.Action> {
    return (_dispatch, getState) => {
        deferTillUserIsAvailable(getState, () => {
            const currentUserId = getState().currentUser.currentUser.user
            void REST.post(AUDIT_ENDPOINT, {
                type: auditType,
                message: `Clicked interlink ${detailId} to ${targetPage} page`,
                source: AuditLogSourceType.UI,
                userId: currentUserId,
            })
        })
    }
}

export function logSavedFilterUsage(
    currentPage: AuditLogPageType,
    auditType: AuditLogAuditType,
): ThunkAction<void, AppState, Api, Actions.Action> {
    return (_dispatch, getState) => {
        deferTillUserIsAvailable(getState, () => {
            const currentUserId = getState().currentUser.currentUser.user
            void REST.post(AUDIT_ENDPOINT, {
                type: auditType,
                message: `${auditType} on ${currentPage} page`,
                source: AuditLogSourceType.UI,
                userId: currentUserId,
            })
        })
    }
}

export function logDownloadFile(
    currentPage: AuditLogPageType,
    detailId: GuidType,
    fileType: DownloadFileType,
): ThunkAction<void, AppState, Api, Actions.Action> {
    return (_dispatch, getState) => {
        deferTillUserIsAvailable(getState, () => {
            const currentUserId = getState().currentUser.currentUser.user
            void REST.post(AUDIT_ENDPOINT, {
                type: AuditLogAuditType.DOWNLOAD_FILE,
                message: `Download ${fileType} file for ${detailId} on ${currentPage}`,
                source: AuditLogSourceType.UI,
                userId: currentUserId,
            })
        })
    }
}
export function logSortByColumn(
    newSortColumn: SortColumnType,
): ThunkAction<void, AppState, Api, Actions.Action> {
    return (_dispatch, getState) => {
        deferTillUserIsAvailable(getState, () => {
            const currentUserId = getState().currentUser.currentUser.user
            void REST.post(AUDIT_ENDPOINT, {
                type: AuditLogAuditType.SORT_BY_COLUMN,
                message: `Sort by ${newSortColumn.orderBy} in ${
                    newSortColumn.orderAscending ? `ascending` : `descending`
                } direction on Vessels Beta Page`,
                source: AuditLogSourceType.UI,
                userId: currentUserId,
            })
        })
    }
}
export function logAnalysisPeriod(
    analysisPeriod: TimestampFilterType,
    selectedColumns: PossibleColumnsVesselBeta[],
): ThunkAction<void, AppState, Api, Actions.Action> {
    return (_dispatch, getState) => {
        deferTillUserIsAvailable(getState, () => {
            const currentUserId = getState().currentUser.currentUser.user
            void REST.post(AUDIT_ENDPOINT, {
                type: AuditLogAuditType.SET_ANALYSIS_PERIOD,
                message: `Change analysis period to ${getTypeDisplayName(
                    analysisPeriod,
                )} for ${selectedColumns.join(', ')}} on Vessels Beta Page`,
                source: AuditLogSourceType.UI,
                userId: currentUserId,
            })
        })
    }
}

export function logAnalysisPeriodOnFilter(
    analysisPeriod: string,
    analysisType: string,
    currentPage: AuditLogPageType,
): ThunkAction<void, AppState, Api, Actions.Action> {
    return (_dispatch, getState) => {
        deferTillUserIsAvailable(getState, () => {
            const currentUserId = getState().currentUser.currentUser.user
            void REST.post(AUDIT_ENDPOINT, {
                type: AuditLogAuditType.SET_ANALYSIS_PERIOD,
                message: `Change analysis period to ${analysisPeriod} for ${analysisType} on ${currentPage} Page`,
                source: AuditLogSourceType.UI,
                userId: currentUserId,
            })
        })
    }
}

export function logAnalysisTypeOnFilter(
    analysisType: string,
    currentPage: AuditLogPageType,
): ThunkAction<void, AppState, Api, Actions.Action> {
    return (_dispatch, getState) => {
        deferTillUserIsAvailable(getState, () => {
            const currentUserId = getState().currentUser.currentUser.user
            void REST.post(AUDIT_ENDPOINT, {
                type: AuditLogAuditType.SET_ANALYSIS_TYPE,
                message: `Change analysis type to ${analysisType} on ${currentPage} Page`,
                source: AuditLogSourceType.UI,
                userId: currentUserId,
            })
        })
    }
}

export function logFilterByTypes(
    type: string,
    typeOption: string,
    typesToDisplay: boolean,
    currentPage: AuditLogPageType | PageType,
): ThunkAction<void, AppState, Api, Actions.Action> {
    return (_dispatch, getState) => {
        deferTillUserIsAvailable(getState, () => {
            const currentUserId = getState().currentUser.currentUser.user
            void REST.post(AUDIT_ENDPOINT, {
                type: AuditLogAuditType.SET_FILTER_BY_TYPE,
                message: `Set ${type}: ${typeOption} to be ${
                    typesToDisplay ? `included` : `excluded`
                } on filter on ${currentPage} Page`,
                source: AuditLogSourceType.UI,
                userId: currentUserId,
            })
        })
    }
}

export function logSelectedColumns(
    selectedColumn: PossibleColumnsVesselBeta,
    columnToDisplay: boolean,
): ThunkAction<void, AppState, Api, Actions.Action> {
    return (_dispatch, getState) => {
        deferTillUserIsAvailable(getState, () => {
            const currentUserId = getState().currentUser.currentUser.user
            void REST.post(AUDIT_ENDPOINT, {
                type: AuditLogAuditType.SET_COLUMN_TO_BE_DISPLAYED,
                message: `Set column: ${selectedColumn} to be ${
                    columnToDisplay ? `displayed` : `removed`
                } on Vessels Beta Page`,
                source: AuditLogSourceType.UI,
                userId: currentUserId,
            })
        })
    }
}
export function logInterlinksClickForVesselsBeta(
    targetPage: AuditLogPageType,
    detailId: GuidType,
    column: string,
): ThunkAction<void, AppState, Api, Actions.Action> {
    return (_dispatch, getState) => {
        deferTillUserIsAvailable(getState, () => {
            const currentUserId = getState().currentUser.currentUser.user
            void REST.post(AUDIT_ENDPOINT, {
                type: AuditLogAuditType.INTERLINK_CLICK,
                message: `Clicked interlink ${detailId} on ${column} column to ${targetPage} page`,
                source: AuditLogSourceType.UI,
                userId: currentUserId,
            })
        })
    }
}
export function logChangeAssetValues(
    assetValue: NodeValue | undefined,
    hostName: string | null,
    detailId: string | null,
): ThunkAction<void, AppState, Api, Actions.Action> {
    return (_dispatch, getState) => {
        deferTillUserIsAvailable(getState, () => {
            const currentUserId = getState().currentUser.currentUser.user
            let message = 'Change assets data:'

            if (assetValue !== undefined) {
                message += ` new assetValue: ${assetValue}`
            }

            if (hostName !== null) {
                message += ` new hostName: ${hostName}`
            }
            void REST.post(AUDIT_ENDPOINT, {
                type: AuditLogAuditType.SET_CHANGE_ASSET_VALUES,
                message: detailId ? `${message} for ${detailId}` : message,
                source: AuditLogSourceType.UI,
                userId: currentUserId,
            })
        })
    }
}
export function logDownloadPolicyFile(
    detailId: GuidType,
    fileType: DownloadFileType,
    policyId: GuidType,
    currentPage: string,
): ThunkAction<void, AppState, Api, Actions.Action> {
    return (_dispatch, getState) => {
        deferTillUserIsAvailable(getState, () => {
            const currentUserId = getState().currentUser.currentUser.user
            void REST.post(AUDIT_ENDPOINT, {
                type: AuditLogAuditType.DOWNLOAD_FILE,
                message: `Download ${fileType} file: ${detailId} for ${currentPage} : ${policyId}`,
                source: AuditLogSourceType.UI,
                userId: currentUserId,
            })
        })
    }
}
export function logButtonClickFor(
    eventClicked: string,
    currentPage: string,
): ThunkAction<void, AppState, Api, Actions.Action> {
    return (_dispatch, getState) => {
        deferTillUserIsAvailable(getState, () => {
            const currentUserId = getState().currentUser.currentUser.user
            void REST.post(AUDIT_ENDPOINT, {
                type: AuditLogAuditType.BUTTON_CLICK,
                message: `Clicked button to ${eventClicked} on ${currentPage} page`,
                source: AuditLogSourceType.UI,
                userId: currentUserId,
            })
        })
    }
}

export function logEventFor(
    eventClicked: string,
    currentPage: string,
    additionalIdentifiers?: string,
): ThunkAction<void, AppState, Api, Actions.Action> {
    const formattedAdditionalInfo = additionalIdentifiers ? ` ${additionalIdentifiers}` : ''
    return (_dispatch, getState) => {
        deferTillUserIsAvailable(getState, () => {
            const currentUserId = getState().currentUser.currentUser.user
            void REST.post(AUDIT_ENDPOINT, {
                type: AuditLogAuditType.TRIGGER_EVENT,
                message: `Trigger event ${eventClicked} on ${currentPage} page${formattedAdditionalInfo}`,
                source: AuditLogSourceType.UI,
                userId: currentUserId,
            })
        })
    }
}

// My Vessels V3 specific audit logging functions

export function logVesselCardClick(
    locationId: GuidType,
): ThunkAction<void, AppState, Api, Actions.Action> {
    return (_dispatch, getState) => {
        deferTillUserIsAvailable(getState, () => {
            const currentUserId = getState().currentUser.currentUser.user
            void REST.post(AUDIT_ENDPOINT, {
                type: AuditLogAuditType.DETAIL_PANEL_ACCESS,
                message: `Opened ${locationId} Event List on ${AuditLogPageType.NEW_VESSELS}`,
                source: AuditLogSourceType.UI,
                userId: currentUserId,
            })
        })
    }
}

export function logVesselModalTabChange(
    locationId: GuidType,
    tabName: string,
): ThunkAction<void, AppState, Api, Actions.Action> {
    return (_dispatch, getState) => {
        deferTillUserIsAvailable(getState, () => {
            const currentUserId = getState().currentUser.currentUser.user
            const listType = tabName === 'Assets' ? 'Asset List' : 'Event List'
            void REST.post(AUDIT_ENDPOINT, {
                type: AuditLogAuditType.DETAIL_PANEL_ACCESS,
                message: `Opened ${locationId} ${listType} on ${AuditLogPageType.NEW_VESSELS}`,
                source: AuditLogSourceType.UI,
                userId: currentUserId,
            })
        })
    }
}

export function logVesselModalFilter(
    locationId: GuidType,
    filterType: string,
    filterValues: string[],
): ThunkAction<void, AppState, Api, Actions.Action> {
    return (_dispatch, getState) => {
        deferTillUserIsAvailable(getState, () => {
            const currentUserId = getState().currentUser.currentUser.user
            const formattedValues = filterValues.join(', ')
            void REST.post(AUDIT_ENDPOINT, {
                type: AuditLogAuditType.SET_FILTER_BY_TYPE,
                message: `Set ${filterType} {${formattedValues}} to be included on filter on ${locationId} Event List on ${AuditLogPageType.NEW_VESSELS}`,
                source: AuditLogSourceType.UI,
                userId: currentUserId,
            })
        })
    }
}

export function logWarningDetailsOpen(
    warningId: GuidType,
): ThunkAction<void, AppState, Api, Actions.Action> {
    return (_dispatch, getState) => {
        deferTillUserIsAvailable(getState, () => {
            const currentUserId = getState().currentUser.currentUser.user
            void REST.post(AUDIT_ENDPOINT, {
                type: AuditLogAuditType.DETAIL_PANEL_ACCESS,
                message: `Opened ${warningId} Warning from Event List on ${AuditLogPageType.NEW_VESSELS}`,
                source: AuditLogSourceType.UI,
                userId: currentUserId,
            })
        })
    }
}

export function logVesselSearch(
    searchTerm: string,
): ThunkAction<void, AppState, Api, Actions.Action> {
    return (_dispatch, getState) => {
        deferTillUserIsAvailable(getState, () => {
            const currentUserId = getState().currentUser.currentUser.user
            void REST.post(AUDIT_ENDPOINT, {
                type: AuditLogAuditType.SET_FILTER_BY_TYPE,
                message: `Set Vessel ${searchTerm} to be included on filter on ${AuditLogPageType.NEW_VESSELS}`,
                source: AuditLogSourceType.UI,
                userId: currentUserId,
            })
        })
    }
}

export function logScoreHover(
    locationId: GuidType,
): ThunkAction<void, AppState, Api, Actions.Action> {
    return (_dispatch, getState) => {
        deferTillUserIsAvailable(getState, () => {
            const currentUserId = getState().currentUser.currentUser.user
            void REST.post(AUDIT_ENDPOINT, {
                type: AuditLogAuditType.HOVER,
                message: `Hovered on score on Vessel ${locationId} card`,
                source: AuditLogSourceType.UI,
                userId: currentUserId,
            })
        })
    }
}

export function logAssetHover(assetId: GuidType): ThunkAction<void, AppState, Api, Actions.Action> {
    return (_dispatch, getState) => {
        deferTillUserIsAvailable(getState, () => {
            const currentUserId = getState().currentUser.currentUser.user
            void REST.post(AUDIT_ENDPOINT, {
                type: AuditLogAuditType.HOVER,
                message: `Hovered on asset ${assetId}`,
                source: AuditLogSourceType.UI,
                userId: currentUserId,
            })
        })
    }
}

export function logAssetEventButtonClick(
    assetId: GuidType,
    buttonType: string,
): ThunkAction<void, AppState, Api, Actions.Action> {
    return (_dispatch, getState) => {
        deferTillUserIsAvailable(getState, () => {
            const currentUserId = getState().currentUser.currentUser.user
            void REST.post(AUDIT_ENDPOINT, {
                type: AuditLogAuditType.BUTTON_CLICK,
                message: `Clicked button on ${assetId} asset hover for ${buttonType} events`,
                source: AuditLogSourceType.UI,
                userId: currentUserId,
            })
        })
    }
}

export function logDeletePolicy(
    policyName: string,
    success: boolean,
): ThunkAction<void, AppState, Api, Actions.Action> {
    return (_dispatch, getState) => {
        deferTillUserIsAvailable(getState, () => {
            const currentUserId = getState().currentUser.currentUser.user

            void REST.post(AUDIT_ENDPOINT, {
                type: AuditLogAuditType.BUTTON_CLICK,
                message: success
                    ? `Policy ${policyName} deleted`
                    : `Failed to delete policy ${policyName}`,
                source: AuditLogSourceType.UI,
                userId: currentUserId,
            })
        })
    }
}
